// src/components/dashboard/OperationsSummary.js

/**
 * The operational strip at the top of a dashboard.
 *
 * Same contract as the workspace command bar's strip: facts, not decoration. It
 * replaced a gradient "Summary" panel that restated the three numbers already
 * printed in the page header directly above it.
 *
 * Cells with nothing to report are dropped rather than rendered as a zero, so
 * the strip stays short on a quiet day and gets louder as work piles up.
 */

const TONE_CLASSES = {
  default: "text-slate-900",
  attention: "text-amber-700",
  critical: "text-rose-700",
  positive: "text-emerald-700",
};

function MetricCell({ label, value, hint, tone = "default" }) {
  return (
    <div className="px-4 py-3" title={hint}>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </p>
      <p
        className={`mt-1 text-2xl font-semibold tabular-nums ${
          TONE_CLASSES[tone] || TONE_CLASSES.default
        }`}
      >
        {value}
      </p>
      {hint && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

/**
 * @param {object} props
 * @param {string} props.title
 * @param {string} [props.subtitle] One line of plain-language state.
 * @param {Array} props.metrics `{ id, label, value, hint, tone }`. A metric with
 *   a null or undefined value is omitted.
 * @param {React.ReactNode} [props.action] Primary action for this surface.
 */
export default function OperationsSummary({
  title,
  subtitle,
  metrics = [],
  action,
}) {
  const visible = metrics.filter(
    (metric) => metric && metric.value !== null && metric.value !== undefined
  );

  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
          {subtitle && (
            <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>
          )}
        </div>
        {action}
      </div>

      {visible.length > 0 && (
        <div className="grid grid-cols-2 divide-slate-100 sm:grid-cols-3 sm:divide-x lg:grid-cols-6">
          {visible.map((metric) => (
            <MetricCell key={metric.id} {...metric} />
          ))}
        </div>
      )}
    </section>
  );
}
